/**
 * The simulated scanner panel.
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
 *
 * This is a showcase surface: it is what a prospect who has never seen the
 * product watches while someone demonstrates scanning, on a laptop with no
 * scanner attached. It is built to look like a device's own control panel — pick
 * a machine, see what is in the tray, press start, watch sheets arrive — rather
 * than like a developer tool with a progress bar.
 *
 * Everything animated here is driven by CSS. The feed reports a sheet every
 * 375 ms at the fastest model, and React re-renders on each one; the paper
 * animation must not be tied to that, or the motion would step at the sheet rate
 * instead of running smoothly underneath it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useModalFocus } from './use-modal-focus';
import { isTopVisibleDialog } from './dialog-tab-stops';
import { LoaderCircle, ScanLine, Square, TriangleAlert, X } from 'lucide-react';
// Photographs of the three models, supplied by Ademero and prepared for the
// picker: background flood-filled away from the borders inward, so the white
// bodies of the smaller two survive while the backdrop does not become a white
// slab on the dark theme; then trimmed to content and scaled 0.78 / 0.90 / 1.00
// so the tray order is visible at a glance rather than only readable from the
// speed label. The white studio floor under each machine was then turned into a
// true shadow (black at alpha 255 - luminance, identical on white), so it no
// longer shows as a pale cut-out on the dark theme (2026-09-27). Build-time
// assets from this repository, never runtime content.
import breezeIllustration from './assets/scanners/breeze.png';
import tornadoIllustration from './assets/scanners/tornado.png';
import type { PublishNotice } from './WorkspaceNotice';
import hurricaneIllustration from './assets/scanners/hurricane.png';
import type {
  SimulatedScanProgress,
  SimulatedScannerCatalog,
  SimulatedScannerModel,
  StagedCapture,
} from '../shared/desktop-api';

const illustrations: Record<string, string> = {
  breeze: breezeIllustration,
  tornado: tornadoIllustration,
  hurricane: hurricaneIllustration,
};

// Rendered height in CSS pixels. Stated here rather than left to `max-height`,
// which would flatten all three to one size and throw away the only cue that
// says at a glance which machine is the big one. The assets are drawn at twice
// these numbers so they stay sharp on a high-density display.
const illustrationHeights: Record<string, number> = {
  breeze: 103,
  tornado: 119,
  hurricane: 132,
};

/** Expected run time for a full tray, so the operator knows what they are starting. */
const estimate = (model: SimulatedScannerModel, pageCount: number): string => {
  const seconds = (pageCount / model.pagesPerMinute) * 60;
  if (seconds < 90) return `${Math.round(seconds)} seconds`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return rest === 0 ? `${minutes} minutes` : `${minutes} min ${rest} s`;
};

export function SimulatedScannerModal({
  jobId,
  initialStack = null,
  stackLocked = false,
  autoStart = false,
  documents,
  onClose,
  onCaptured,
  onToast,
}: {
  jobId: string;
  /** The stack to load first: a sample job's set, or the set that fits the job's kind. */
  initialStack?: string | null;
  /** A sample job's set is already chosen: its name shows, with no second picker. */
  stackLocked?: boolean;
  /** "Try sample paperwork": skip the chooser and feed at once on the default model. */
  autoStart?: boolean;
  /** Only these files of the stack: the tour's chosen documents. */
  documents?: string[];
  onClose: () => void;
  onCaptured: (capture: StagedCapture) => void;
  onToast: PublishNotice;
}) {
  const [catalog, setCatalog] = useState<SimulatedScannerCatalog | null>(null);
  /** The stack the catalog on screen was read for; undefined until one is read. */
  const [catalogStack, setCatalogStack] = useState<string | null | undefined>(undefined);
  const dialogRef = useModalFocus();
  // The fastest feeder by default: a trial's first scan should not make anyone wait
  // (owner, 2026-09-27: 106 sheets at 60 pages/min was far too slow).
  const [selected, setSelected] = useState<string>('hurricane');
  /**
   * Which stack to load, or null for whatever the product would reach for.
   *
   * A choice exists at all because not having one wasted a real testing session:
   * somebody who came to look at accounts payable was handed freight logistics.
   * The dialog named the stack correctly the whole time — it simply offered no way
   * to load a different one.
   */
  const [stack, setStack] = useState<string | null>(initialStack);
  const [progress, setProgress] = useState<SimulatedScanProgress | null>(null);
  const [scanning, setScanning] = useState(false);
  const [sheetSource, setSheetSource] = useState<string | null>(null);
  const [sheet, setSheet] = useState<string | null>(null);
  const autoStarted = useRef(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState('');
  const scanningRef = useRef(false);

  const desktop = window.capturePointDesktop;

  useEffect(() => {
    let live = true;
    desktop
      ?.getSimulatedScanners(stack ?? undefined)
      .then((result) => {
        if (!live) return;
        setCatalog(result);
        setCatalogStack(stack);
      })
      .catch(() => live && setError('The simulated scanner could not be prepared.'));
    return () => {
      live = false;
    };
    // Re-read on a change of stack: the sheet count is only known once the chosen
    // stack's pages have been opened, and it is the number the operator is about to
    // wait for.
  }, [desktop, stack]);

  const previewPage = useRef(0);
  useEffect(
    () =>
      desktop?.onSimulatedScanProgress((update) => {
        if (update.jobId !== jobId) return;
        // A sheet is reported twice: once when it lands, then again with its image.
        // Keep the highest page seen so the later, slower image event cannot make the
        // counter appear to run backwards.
        setProgress((current) => (current && current.page > update.page ? { ...current } : update));
        // Pictures render in parallel and can finish out of order; an older sheet's
        // picture never replaces a newer one's.
        if (update.page === 1 && !update.previewPath) previewPage.current = 0; // a new run
        if (update.previewPath && update.page > previewPage.current) {
          previewPage.current = update.page;
          setSheetSource(update.previewPath);
        }
      }),
    [desktop, jobId],
  );

  // Read the rendered sheet. Only the newest one is held: this is a feed, and an
  // operator watching a scanner sees the page going through it, not a gallery.
  useEffect(() => {
    if (!sheetSource || !desktop) return;
    let canceled = false;
    void desktop
      .readPagePreview(sheetSource)
      .then((source) => {
        if (!canceled) setSheet(source);
      })
      .catch(() => undefined);
    return () => {
      canceled = true;
    };
  }, [sheetSource, desktop]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === 'Escape' &&
        !event.defaultPrevented &&
        isTopVisibleDialog(dialogRef.current) &&
        !scanningRef.current
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const model = useMemo(
    () =>
      catalog?.models.find((candidate) => candidate.id === selected) ?? catalog?.models[1] ?? null,
    [catalog, selected],
  );
  const tray = catalog?.tray ?? null;
  // A newly chosen stack is read before it can be fed: until then the tray on
  // screen is the previous stack's, and Start fed that one. Compared with the
  // stack the catalog was read for, not with the tray's own corpus, because the
  // main process can describe a different stack when the chosen one is missing.
  const stackLoading = catalogStack !== stack;

  const start = async () => {
    if (!desktop || !model || !tray || scanning || stackLoading) return;
    setError('');
    setProgress(null);
    setScanning(true);
    scanningRef.current = true;
    try {
      const result = await desktop.startSimulatedScan({
        jobId,
        modelId: model.id,
        corpus: tray.corpus,
        documents,
      });
      if (result.capture) {
        onCaptured(result.capture);
        onToast(
          result.stopped
            ? `Scan stopped - ${result.capture.fileCount} sheet${result.capture.fileCount === 1 ? '' : 's'} kept.`
            : `${result.capture.fileCount} sheets scanned at ${result.achievedPagesPerMinute ?? model.pagesPerMinute} pages per minute.`,
          // The person's own stop is not a failure, whatever "stopped" reads as.
          result.stopped ? 'notice' : undefined,
        );
        onClose();
      } else if (!result.canceled) {
        setError('The simulated scanner produced no pages.');
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The simulated scan failed.');
    } finally {
      scanningRef.current = false;
      setScanning(false);
      setStopping(false);
    }
  };

  // A sample job has nothing to choose: its set is fixed and the default model
  // feeds at once, so the first export is one click from the welcome.
  useEffect(() => {
    if (
      !autoStart ||
      autoStarted.current ||
      !catalog ||
      !tray ||
      !model ||
      scanning ||
      stackLoading
    )
      return;
    autoStarted.current = true;
    void start();
    // start reads the current catalog, tray and model; it runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, catalog, tray, model, stackLoading]);

  const stop = async () => {
    if (!desktop || stopping) return;
    setStopping(true);
    await desktop.stopSimulatedScan({ jobId }).catch(() => undefined);
  };

  const pageCount = progress?.pageCount ?? tray?.pageCount ?? 0;
  const scanned = progress?.page ?? 0;
  // Paper is moving, or has moved: the dialog is a scanner now, not a chooser.
  const feeding = scanning || progress !== null;

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && !scanning && onClose()}
    >
      <section
        ref={dialogRef}
        tabIndex={-1}
        className={`sim-scanner-modal ${feeding ? 'feeding' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sim-scanner-title"
      >
        <header className="sim-scanner-head">
          <div>
            {/* The model is context for the title, not a heading on its own: alone in the
                rail, "Ademero Hurricane" meant nothing to a first-time user (owner, 2026-09-27). */}
            <h2 id="sim-scanner-title">
              {feeding && model ? `Simulated scanner · ${model.name}` : 'Simulated scanner'}
            </h2>
            {!autoStart && <p>Choose a scanner speed.</p>}
          </div>
          <button
            className="icon-button"
            onClick={onClose}
            disabled={scanning}
            aria-label="Close simulated scanner"
          >
            <X size={17} />
          </button>
        </header>

        <div className="sim-scanner-body">
          {/* Once paper is moving, the picker has nothing left to offer and the
              page does: an operator at a real scanner watches sheets go through it.
              Hiding the models also takes the dialog well under the height that was
              making it scroll. */}
          {feeding ? (
            /* Owner, 2026-09-27: the real page is the show, front and centre, sliding in as
               each sheet is scanned; the scanner is a small still picture beside it (an
               animated cut-out over the photo looked jagged and fake). */
            <div className="sim-scanner-stage paper">
              <div className="sim-scanner-page-stage">
                {sheet ? (
                  <CalmPage sheet={sheet} label={`Sheet ${progress?.page ?? 1}`} />
                ) : (
                  <span className="sim-scanner-rail-waiting">
                    <LoaderCircle className="spin" size={16} /> Feeding the first sheet
                  </span>
                )}
              </div>
              <aside className="sim-scanner-rail" aria-live="polite">
                {illustrations[model?.id ?? selected] && (
                  <div className="sim-scanner-rail-machine">
                    <img src={illustrations[model?.id ?? selected]} alt="" />
                  </div>
                )}
                <span className="sim-scanner-rail-count">
                  <strong>{scanned}</strong> / {pageCount}
                </span>
                <span className="sim-scanner-rail-bar" aria-hidden="true">
                  <i
                    style={{
                      width: `${pageCount > 0 ? Math.min(100, (scanned / pageCount) * 100) : 0}%`,
                    }}
                  />
                </span>
                <span className="sim-scanner-rail-speed">
                  {progress?.pagesPerMinute ?? model?.pagesPerMinute ?? 0} pages / min
                </span>
                {error && (
                  <div className="modal-error">
                    <TriangleAlert size={15} /> {error}
                  </div>
                )}
                <button
                  className="secondary-button sim-scanner-rail-stop"
                  onClick={() => void stop()}
                  disabled={!scanning || stopping}
                >
                  {stopping ? <LoaderCircle className="spin" size={15} /> : <Square size={14} />}{' '}
                  {stopping ? 'Stopping…' : 'Stop'}
                </button>
              </aside>
            </div>
          ) : (
            <div className="sim-scanner-models" role="radiogroup" aria-label="Scanner model">
              {(catalog?.models ?? []).map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  role="radio"
                  aria-checked={candidate.id === model?.id}
                  className={`sim-scanner-model ${candidate.id === model?.id ? 'selected' : ''} ${scanning && candidate.id === model?.id ? 'feeding' : ''}`}
                  disabled={scanning}
                  onClick={() => setSelected(candidate.id)}
                >
                  <span className="sim-scanner-art" aria-hidden="true">
                    <img
                      src={illustrations[candidate.id]}
                      alt=""
                      draggable={false}
                      style={{ height: `${illustrationHeights[candidate.id] ?? 120}px` }}
                    />
                  </span>
                  <span className="sim-scanner-name">{candidate.name}</span>
                  <span className="sim-scanner-tier">{candidate.tier}</span>
                  <span className="sim-scanner-speed">
                    <strong>{candidate.pagesPerMinute}</strong> pages / min
                  </span>
                </button>
              ))}
              {!catalog && (
                <div className="sim-scanner-loading">
                  <LoaderCircle className="spin" size={18} /> Preparing the demonstration tray…
                </div>
              )}
            </div>
          )}

          {!feeding && (
            <div className="sim-scanner-tray">
              {tray ? (
                <>
                  <span className="sim-scanner-tray-title">
                    {stackLocked
                      ? (catalog?.stacks?.find((candidate) => candidate.corpus === tray.corpus)
                          ?.label ?? 'Sample tray')
                      : 'Sample tray'}
                  </span>
                  <p>
                    <strong>{tray.pageCount} sheets</strong> ·{' '}
                    <strong>{tray.documentCount} documents</strong>
                    {model && !scanning && <> · about {estimate(model, tray.pageCount)}</>}
                  </p>
                  {/* The tray holds a fixed number of sheets, so a large stack does not
                  all fit. Unsaid, the two numbers on this panel simply disagreed:
                  the picker below offered "Freight Logistics — 136 documents" and
                  the line above it said 61, in the same unit, a centimetre apart.
                  A stack that does not fit is a fact about the tray, and saying so
                  is what makes both numbers true instead of one of them wrong. */}
                  {(() => {
                    const stack = catalog?.stacks?.find(
                      (candidate) => candidate.corpus === tray.corpus,
                    );
                    const left = (stack?.documentCount ?? 0) - tray.documentCount;
                    return (
                      left > 0 && (
                        <p className="sim-scanner-tray-note">
                          {left} more documents available in this sample set.
                        </p>
                      )
                    );
                  })()}
                  {/* The stack is a choice, not an assignment. It reads as "load a
                  different stack" rather than a bare dropdown because most people
                  opening this dialog want the demonstration and should not have to
                  make a decision to get one. */}
                  {!stackLocked && (catalog?.stacks?.length ?? 0) > 1 && (
                    <label className="sim-scanner-stack">
                      <span>Sample set</span>
                      <select
                        value={stack ?? tray.corpus}
                        disabled={scanning}
                        aria-label="Which stack to load"
                        onChange={(event) => setStack(event.target.value)}
                      >
                        {catalog!.stacks.map((candidate) => (
                          <option key={candidate.corpus} value={candidate.corpus}>
                            {candidate.label} - {candidate.documentCount} documents
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </>
              ) : (
                catalog && (
                  <p className="sim-scanner-empty">
                    <TriangleAlert size={15} /> No sample documents are installed on this computer,
                    so there is nothing to feed.
                  </p>
                )
              )}
            </div>
          )}
        </div>

        {!feeding && error && (
          <div className="modal-error">
            <TriangleAlert size={15} /> {error}
          </div>
        )}

        {!feeding && (
          <footer className="modal-actions">
            {scanning ? (
              <button className="secondary-button" onClick={() => void stop()} disabled={stopping}>
                {stopping ? <LoaderCircle className="spin" size={15} /> : <Square size={14} />} Stop
              </button>
            ) : (
              <button className="secondary-button" onClick={onClose}>
                Cancel
              </button>
            )}
            <button
              className="primary-button"
              onClick={() => void start()}
              disabled={scanning || !model || !tray || stackLoading}
            >
              {scanning ? <LoaderCircle className="spin" size={15} /> : <ScanLine size={16} />}
              {scanning ? 'Scanning…' : 'Start scanning'}
            </button>
          </footer>
        )}
      </section>
    </div>
  );
}

/**
 * The scanned page, shown at the scanner's own pace: every sheet the counter counts lands on
 * screen as it is counted (owner, 2026-09-27: pages held back to a calm 700 ms while the count
 * ran ahead at 0.4 s a sheet looked fake). What strobed before was each image fading up from
 * nothing, so a new sheet slides in quickly on top of the one it replaces and there is never
 * a blank frame.
 */
function CalmPage({ sheet, label }: { sheet: string; label: string }) {
  const [shown, setShown] = useState<{ current: string; previous: string | null }>({
    current: sheet,
    previous: null,
  });
  useEffect(() => {
    setShown((state) =>
      state.current === sheet ? state : { current: sheet, previous: state.current },
    );
  }, [sheet]);
  return (
    <div className="sim-scanner-page-frame">
      {shown.previous && (
        <img className="sim-scanner-page under" src={shown.previous} alt="" aria-hidden="true" />
      )}
      <img key={shown.current} className="sim-scanner-page over" src={shown.current} alt={label} />
    </div>
  );
}
